import { useEffect, useState } from 'react';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import { HomepageCacheProvider } from './context/HomepageCacheContext';
import UnifiedAccessPortal from './UnifiedAccessPortal';
import HomePage from './HomePage';
import Navbar from './components/Navbar';
import AboutUsPage from './AboutUsPage';
import { mergeAboutUsData } from './data/aboutUsDefaults';
import { mergeEducationData } from './data/educationDefaults';
import { ADMISSIONS_DEFAULTS } from './data/admissionsDefaults';
import { SECONDARY_DEFAULTS } from './data/secondaryDefaults';
import EducationProgramPage from './EducationProgramPage';
import AdmissionsPage from './AdmissionsPage';
import StudentLifePage from './StudentLifePage';
import HouseDetailPage from './HouseDetailPage';
import EventsCompetitionsPage from './EventsCompetitionsPage';
import FacilitiesPage from './FacilitiesPage';
import FacilityDetailPage from './FacilityDetailPage';
import TechnologyDetailPage from './TechnologyDetailPage';
import NewsUpdatesPage from './NewsUpdatesPage';
import ParentsPage from './ParentsPage';
import PaLCDetailPage from './PaLCDetailPage';
import CareersPage from './CareersPage';
import ContactUsPage from './ContactUsPage';
import { cleanGoogleMapsEmbedUrl } from './utils/mapUtils';
import RegisterPage from './RegisterPage';
import NewsDetailPage from './NewsDetailPage';
import NewsAllPage from './NewsAllPage';
import TestContentPage from './pages/TestContentPage';
import PreschoolPage from './PreschoolPage';
import KindergartenPage from './KindergartenPage';
import PrimaryPage from './PrimaryPage';
import SecondaryPage from './SecondaryPage';
import LoginGateway from './LoginGateway';
import WebsiteAdminDashboard from './dashboards/WebsiteAdminDashboard';
import PrincipalDashboard from './dashboards/PrincipalDashboard';
import AdmissionsAdminDashboard from './dashboards/AdmissionsAdminDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import ParentDashboard from './ParentDashboard';
import StaffDashboard from './StaffDashboard';
import { Mail, Send, Play, Phone, MessageCircle, MapPin } from 'lucide-react';

const API_BASE = (() => {
  // Try env variable first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect based on current domain
  const protocol = window.location.protocol; // http: or https:
  const hostname = window.location.hostname; // localhost, example.com, etc
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Development - point to PRODUCTION API at /api
    return 'https://permataharapansecondary.sch.id/api';
  }
  
  // Production - API on same domain under /api
  return `${protocol}//${hostname}/api`;
})();

console.log('[App] API_BASE configured as:', API_BASE);

function App() {
  // Handle path-based and hash-based routing
  const getInitialSlug = () => {
    const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
    if (pathname === '/login') {
      return 'login';
    }
    if (pathname === '/register') {
      return 'register';
    }
    if (pathname === '/director-dashboard') {
      return 'director-dashboard';
    }
    if (pathname === '/principal-dashboard' || pathname === '/principal_dashboard') {
      return 'principal-dashboard';
    }
    if (pathname === '/website-admin-cms') {
      return 'website-admin-cms';
    }
    if (pathname === '/administrator') {
      return 'administrator';
    }
    if (pathname === '/admissions-dashboard') {
      return 'admissions-dashboard';
    }
    const hash = window.location.hash.replace('#', '');
    if (hash === 'register') {
      return 'register';
    }
    if (hash === 'login') {
      return 'login';
    }
    if (hash === 'director-dashboard') {
      return 'director-dashboard';
    }
    if (hash === 'parent-dashboard') {
      return 'parent-dashboard';
    }
    if (hash === 'staff-dashboard') {
      return 'staff-dashboard';
    }
    if (hash.startsWith('news/')) {
      return hash;
    }
    if (hash.startsWith('news-blog-') || hash.startsWith('news-press-')) {
      return hash;
    }
    if (hash === 'news-and-updates' || hash === 'news-updates') {
      return 'news-and-updates';
    }
    if (hash === 'news-all') {
      return 'news-all';
    }
    return 'homepage';
  };
  
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const isLoginPath = normalizedPath === '/login';
  const isRegisterPath = normalizedPath === '/register';
  const isAdministratorPath = normalizedPath === '/administrator';
  const isAdmissionsPath = normalizedPath === '/admissions';
  const isDirectorPath = normalizedPath === '/director-dashboard';
  const isPrincipalPath = normalizedPath === '/principal-dashboard' || normalizedPath === '/principal_dashboard';
  const isWebsiteAdminPath = normalizedPath === '/website-admin-cms';
  const isAdmissionsDashboardPath = normalizedPath === '/admissions-dashboard';
  const isStaffDashboardPath = normalizedPath === '/staff-dashboard';
  const isParentDashboardPath = normalizedPath === '/parent-dashboard';
  const isUnifiedGatePath = isAdmissionsPath;
  const isDashboardPath = isAdministratorPath || isDirectorPath || isPrincipalPath || isWebsiteAdminPath || isAdmissionsDashboardPath || isStaffDashboardPath || isParentDashboardPath;

  const [menu, setMenu] = useState([]);
  const [language, setLanguage] = useState(() => {
    const saved = window.localStorage.getItem('phs-language');
    return saved === 'id' ? 'id' : 'en';
  });
  const [activeSlug, setActiveSlug] = useState(getInitialSlug());
  const [activeItemId, setActiveItemId] = useState(null);
  const [page, setPage] = useState(() => {
    const slug = getInitialSlug();
    if (['about-us', 'education-program', 'secondary', 'admissions', 'student-life', 'facilities', 'news-and-updates', 'news-updates', 'parents', 'careers', 'contact-us'].includes(slug)) {
      try {
        const cached = sessionStorage.getItem('cms_page_' + slug);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed._slug === slug) return parsed;
        }
      } catch (e) {}
    }
    return null;
  });
  const [pageSource, setPageSource] = useState(null);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingPage, setLoadingPage] = useState(() => {
    const slug = getInitialSlug();
    if (['about-us', 'education-program', 'secondary', 'admissions', 'student-life', 'facilities', 'news-and-updates', 'news-updates', 'parents', 'careers', 'contact-us'].includes(slug)) {
      try {
        const cached = sessionStorage.getItem('cms_page_' + slug);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed._slug === slug) return false;
        }
      } catch (e) {}
      return true;
    }
    return false;
  });
  const [error, setError] = useState('');
  const [pendingAnchor, setPendingAnchor] = useState('');
  const [pageRefreshCounter, setPageRefreshCounter] = useState(0);
  // Cache CMS sections removed - always fetch fresh data to show latest approved content
  const [cmsCache, setCmsCache] = useState(null);

  // REMOVED: Cache clearing event listener - no longer using cmsCache for pages

  // PRELOAD REMOVED: HomePage will handle all fetching independently
  // This eliminates race conditions and redundant requests

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    // Handle hash and popstate changes for routing
    const handleLocationChange = () => {
      const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
      const hash = window.location.hash.replace('#', '');
      if (pathname === '/login' || hash === 'login') {
        setActiveSlug('login');
        setActiveItemId(null);
      } else if (pathname === '/register' || hash === 'register') {
        setActiveSlug('register');
        setActiveItemId(null);
      } else if (pathname === '/director-dashboard' || hash === 'director-dashboard') {
        setActiveSlug('director-dashboard');
        setActiveItemId(null);
      } else if (pathname === '/principal-dashboard' || pathname === '/principal_dashboard' || hash === 'principal-dashboard' || hash === 'principal_dashboard') {
        setActiveSlug('principal-dashboard');
        setActiveItemId(null);
      } else if (pathname === '/website-admin-cms' || hash === 'website-admin-cms') {
        setActiveSlug('website-admin-cms');
        setActiveItemId(null);
      } else if (pathname === '/administrator' || hash === 'administrator') {
        setActiveSlug('administrator');
        setActiveItemId(null);
      } else if (pathname === '/admissions-dashboard' || hash === 'admissions-dashboard') {
        setActiveSlug('admissions-dashboard');
        setActiveItemId(null);
      } else if (pathname === '/parent-dashboard' || hash === 'parent-dashboard') {
        setActiveSlug('parent-dashboard');
        setActiveItemId(null);
      } else if (pathname === '/staff-dashboard' || hash === 'staff-dashboard') {
        setActiveSlug('staff-dashboard');
        setActiveItemId(null);
      } else if (hash.startsWith('news/')) {
        setActiveSlug(hash);
        setActiveItemId(null);
      } else if (hash.startsWith('news-blog-') || hash.startsWith('news-press-')) {
        setActiveSlug(hash);
        setActiveItemId(null);
      } else if (hash === 'news-and-updates' || hash === 'news-updates') {
        setActiveSlug('news-and-updates');
        setActiveItemId(null);
      } else if (hash === 'news-all') {
        setActiveSlug('news-all');
        setActiveItemId(null);
      } else if (pathname === '/' && hash === '' && (activeSlug === 'register' || activeSlug === 'login' || activeSlug === 'parent-dashboard' || activeSlug === 'staff-dashboard' || activeSlug.startsWith('news/') || activeSlug.startsWith('news-blog-') || activeSlug.startsWith('news-press-') || activeSlug === 'news-all')) {
        setActiveSlug('homepage');
        setActiveItemId(null);
      }
    };
    
    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener('popstate', handleLocationChange);
    return () => {
      window.removeEventListener('hashchange', handleLocationChange);
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, [activeSlug]);
  useEffect(() => {
    window.localStorage.setItem('phs-language', language);
    document.documentElement.lang = language === 'id' ? 'id' : 'en';
  }, [language]);
  useEffect(() => {
    async function loadMenu() {
      setLoadingMenu(true);
      try {
        // Use static menu data instead of fetching from API
        const staticMenu = [
          { id: 1, title: 'About Us', slug: 'about-us' },
          { id: 2, title: 'Education Program', slug: 'education-program' },
          { id: 3, title: 'Admissions', slug: 'admissions' },
          { id: 4, title: 'Student Life', slug: 'student-life' },
          { id: 5, title: 'Facilities', slug: 'facilities' },
          { id: 6, title: 'News and Updates', slug: 'news-and-updates' },
          { id: 7, title: 'Parents', slug: 'parents' },
          { id: 8, title: 'Careers', slug: 'careers' },
          { id: 9, title: 'Contact Us', slug: 'contact-us' },
        ];
        setMenu(staticMenu);
        setError('');
      } catch (err) {
        console.error('Menu setup error:', err);
        setError('');
        setMenu([]);
      } finally {
        setLoadingMenu(false);
      }
    }

    if (isUnifiedGatePath || isDashboardPath) {
      setLoadingMenu(false);
      return;
    }

    loadMenu();
  }, [isUnifiedGatePath, isDashboardPath]);

  useEffect(() => {
    async function loadPage() {
      const staticPages = ['homepage'];

      if (!activeSlug) {
        setLoadingPage(false);
        setPageSource(null);
        setPage(null);
        return;
      }

      // ── CMS pages: fetch from database FIRST, show loading until ready ──
      const cmsSlug = activeSlug;

      if (['about-us', 'education-program', 'secondary', 'admissions', 'student-life', 'facilities', 'news-and-updates', 'news-updates', 'parents', 'careers', 'contact-us'].includes(cmsSlug)) {
        let hasCache = false;
        try {
          const cached = sessionStorage.getItem('cms_page_' + cmsSlug);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && parsed._slug === cmsSlug) {
              setPage(parsed);
              setLoadingPage(false);
              hasCache = true;
            }
          }
        } catch (e) {}

        if (!hasCache) {
          setLoadingPage(true);
          setPage(prev => (prev && prev._slug === cmsSlug ? prev : null));
        }

        try {
          // CRITICAL: NEVER use cmsCache for CMS pages - always fetch fresh data
          // This ensures user always sees the latest approved content
          const controller = new AbortController();
          const t = setTimeout(() => controller.abort(), 15000);
          
          // Map cmsSlug to database page value for API parameter
          const pageParamMap = {
            'about-us': 'aboutus',
            'education-program': 'education-program',
            'secondary': 'secondary',
            'admissions': 'admissions',
            'student-life': 'student-life',
            'facilities': 'facilities',
            'news-and-updates': 'news-updates',
            'news-updates': 'news-updates',
            'parents': 'parents',
            'careers': 'careers',
            'contact-us': 'contact-us',
            'contact': 'contact-us'
          };
          const pageParam = pageParamMap[cmsSlug] || cmsSlug;
          
          const endpointMap = {
            'student-life': `${API_BASE}/studentlife/cms/sections?published=true&_t=${Date.now()}`,
            'education-program': `${API_BASE}/education/cms/sections?published=true&_t=${Date.now()}`,
            'admissions': `${API_BASE}/admissions/cms/sections?published=true&_t=${Date.now()}`,
            'about-us': `${API_BASE}/aboutus/cms/sections?published=true&_t=${Date.now()}`,
            'secondary': `${API_BASE}/homepage/cms/sections?published=true&page=secondary&_t=${Date.now()}`,
            'facilities': `${API_BASE}/homepage/cms/sections?published=true&page=facilities&_t=${Date.now()}`,
            'news-and-updates': `${API_BASE}/homepage/cms/sections?published=true&page=news-updates&_t=${Date.now()}`,
            'news-updates': `${API_BASE}/homepage/cms/sections?published=true&page=news-updates&_t=${Date.now()}`,
            'parents': `${API_BASE}/homepage/cms/sections?published=true&page=parents&_t=${Date.now()}`,
            'careers': `${API_BASE}/homepage/cms/sections?published=true&page=careers&_t=${Date.now()}`,
            'contact-us': `${API_BASE}/homepage/cms/sections?published=true&page=contact-us&_t=${Date.now()}`,
            'contact': `${API_BASE}/homepage/cms/sections?published=true&page=contact-us&_t=${Date.now()}`
          };
          const fetchUrl = endpointMap[cmsSlug] || `${API_BASE}/homepage/cms/sections?published=true&page=${pageParam}&_t=${Date.now()}`;
          console.log(`[App] Fetching CMS sections for page '${pageParam}' from: ${fetchUrl}`);
          
          let sections = null;
          try {
            const response = await fetch(fetchUrl, { 
              credentials: 'omit', 
              signal: controller.signal,
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
              }
            });
            clearTimeout(t);
            
            console.log(`[App] Response status: ${response.status}`);
            if (response.ok) {
              const result = await response.json();
              console.log(`[App] Response ok:`, result.ok);
              console.log(`[App] Response data type:`, Array.isArray(result.data) ? 'array' : typeof result.data);
              console.log(`[App] Response data length:`, result.data?.length);
              sections = Array.isArray(result.data) ? result.data : Object.values(result.data || {});
              console.log(`[App] Parsed sections count: ${sections.length}`);
              sections.forEach(s => console.log(`  - Section type: ${s.section_type}`));
            } else {
              console.error(`[App] API error - status: ${response.status}`);
              const errorText = await response.text();
              console.error(`[App] Error response:`, errorText.substring(0, 200));
            }
          } catch (fetchErr) {
            clearTimeout(t);
            console.error(`[App] Fetch error (timeout or network):`, fetchErr.message);
          }

          // Fallback fetch if dedicated endpoint returned empty
          if ((!sections || sections.length === 0) && cmsSlug !== 'student-life') {
            const fallbackUrl = `${API_BASE}/homepage/cms/sections?published=true&page=${pageParam}&_t=${Date.now()}`;
            console.log(`[App] Trying fallback fetch from: ${fallbackUrl}`);
            try {
              const fbRes = await fetch(fallbackUrl, { credentials: 'omit' });
              if (fbRes.ok) {
                const fbJson = await fbRes.json();
                sections = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
              }
            } catch (fbErr) {
              console.warn('[App] Fallback fetch failed:', fbErr.message);
            }
          }

          if (sections && sections.length > 0) {
            console.log(`[App] Looking for sections from page: ${cmsSlug}`);
            
            // Map cmsSlug to database page value
            const pageMap = {
              'about-us': 'aboutus',
              'education-program': 'education-program',
              'secondary': 'secondary',
              'admissions': 'admissions',
              'news-and-updates': 'news-updates',
              'news-updates': 'news-updates',
              'parents': 'parents',
              'careers': 'careers',
              'contact-us': 'contact-us',
              'contact': 'contact-us'
            };
            const dbPage = pageMap[cmsSlug] || cmsSlug;
            console.log(`[App] Mapped to database page: ${dbPage}`);
            
            // Dedicated endpoints return only this page's sections
            const isDedicated = ['student-life', 'education-program', 'admissions', 'about-us'].includes(cmsSlug);
            const pageSections = isDedicated
              ? sections
              : sections.filter(s => {
                  return s.page === dbPage || 
                         s.section_type?.startsWith(dbPage + '-') ||
                         s.section_type?.startsWith('news-') ||
                         s.section_type?.startsWith('parents-') ||
                         s.section_type?.startsWith('careers-') ||
                         s.section_type?.startsWith('contact-') ||
                         s.section_type === cmsSlug;
                });
            
            console.log(`[App] Found ${pageSections.length} sections for page ${cmsSlug}`);
            pageSections.forEach(s => console.log(`  - ${s.section_type}`));
            
            if (pageSections.length > 0) {
              // Merge all sections data for this page
              let mergedContent = { en: {}, id: {} };
              const sectionMap = {};
              
              pageSections.forEach(section => {
                const cp = (section.content_published && section.content_published !== '{}' && section.content_published !== '[]') 
                  ? section.content_published 
                  : (section.content || section.content_published);
                let parsed = null;
                try {
                  parsed = typeof cp === 'string' ? JSON.parse(cp) : cp;
                } catch (e) {
                  parsed = cp;
                }
                const st = section.section_type;
                if (parsed) {
                  const contentEn = parsed.en || parsed;
                  const contentId = parsed.id;
                  
                  // Smart merge into sectionMap[st] to prevent empty rows from overwriting populated rows
                  if (!sectionMap[st]) {
                    sectionMap[st] = contentEn;
                  } else {
                    const existing = sectionMap[st];
                    const merged = { ...existing };
                    for (const [k, v] of Object.entries(contentEn)) {
                      if (Array.isArray(v)) {
                        const existingHasImages = Array.isArray(existing[k]) && existing[k].some(item => item && typeof item === 'object' && (item.image || item.url || item.image_url));
                        const newHasImages = v.some(item => item && typeof item === 'object' && (item.image || item.url || item.image_url));

                        if (newHasImages || !existingHasImages) {
                          if (v.length > 0 || !Array.isArray(existing[k]) || existing[k].length === 0) {
                            merged[k] = v;
                          }
                        }
                      } else if (v !== null && v !== undefined && v !== '') {
                        merged[k] = v;
                      }
                    }
                    sectionMap[st] = merged;
                  }

                  // Merge all language versions
                  if (parsed.en) {
                    mergedContent.en = { ...mergedContent.en, ...parsed.en };
                  } else {
                    mergedContent.en = { ...mergedContent.en, ...parsed };
                  }
                  if (contentId) mergedContent.id = { ...mergedContent.id, ...contentId };
                }
              });
              
              console.log(`[App] Merged content for ${cmsSlug}:`, mergedContent.en);
              let pageData;
              if (cmsSlug === 'about-us') {
                const heroSec = sectionMap['about-us-hero-shell'] || {};
                const histSec = sectionMap['about-us-history-shell'] || {};
                const whySec = sectionMap['about-us-why-started-shell'] || {};
                const estSec = sectionMap['about-us-established-shell'] || {};
                const princSec = sectionMap['about-us-principal-shell'] || sectionMap['about-us-principal-message'] || {};
                const leadSec = sectionMap['about-us-leadership-shell'] || {};
                const philSec = sectionMap['about-us-philosophy-shell'] || {};
                const valSec = sectionMap['about-us-values-shell'] || {};

                const transformedData = {
                  ...mergedContent?.en,
                  hero_title: heroSec.title,
                  hero_summary: heroSec.summary,
                  hero_slides: heroSec.slides ? heroSec.slides.map(s => (s && typeof s === 'object') ? (s.image || s.url || s.video || s) : s) : undefined,
                  hero_video: heroSec.video || heroSec.video_url || heroSec.video_file || heroSec.hero_video,
                  slides: heroSec.slides,
                  history_timeline: histSec.items || histSec.history_timeline,
                  leadership_profiles: leadSec.items || leadSec.leadership_profiles,
                  school_philosophy: philSec.items || philSec.school_philosophy,
                  cards: valSec.cards,
                  values_cards: valSec.cards,
                  values_kicker: valSec.kicker,
                  values_title: valSec.title,
                  values_intro: valSec.intro,
                  why_started: whySec,
                  established: estSec,
                  established_date: estSec.date,
                  established_facts: estSec.facts,
                  established_cta: estSec.cta,
                  // Principal fields
                  principal_name: princSec.name || mergedContent?.en?.name,
                  principal_credentials: princSec.credentials || mergedContent?.en?.credentials,
                  principal_role: princSec.role || mergedContent?.en?.role,
                  principal_opening: princSec.opening || mergedContent?.en?.opening,
                  principal_paragraph_one: princSec.paragraph_one || mergedContent?.en?.paragraph_one,
                  principal_paragraph_two: princSec.paragraph_two || mergedContent?.en?.paragraph_two,
                  principal_closing: princSec.closing || mergedContent?.en?.closing,
                  principal_theme_line: princSec.theme_line || mergedContent?.en?.theme_line,
                  principal_photo: princSec.photo || princSec.principal_photo_url || princSec.principal_photo_id || mergedContent?.en?.principal_photo_url || mergedContent?.en?.principal_photo_id,
                  principal_photo_id: princSec.principal_photo_id || princSec.photo || mergedContent?.en?.principal_photo_id,
                  principal_photo_url: princSec.principal_photo_url || mergedContent?.en?.principal_photo_url,
                  raw_sections: sectionMap
                };

                console.log(`[App] Transformed About Us data from content_published:`, transformedData);
                pageData = Object.keys(transformedData).some(k => transformedData[k] !== undefined && transformedData[k] !== '')
                  ? transformedData
                  : mergeAboutUsData({});
              } else if (cmsSlug === 'education-program') {
                const { mergeEducationData: merge } = await import('./data/educationDefaults');
                const mainEdu = sectionMap['education-program'] || {};
                const heroEdu = sectionMap['education-program-hero'] || {};
                const levelsEdu = sectionMap['education-program-levels-shell'] || {};
                const currEdu = sectionMap['education-curriculum-overview-shell'] || {};
                const subjEdu = sectionMap['education-subjects-offered'] || sectionMap['education-subjects-offered-shell'] || {};
                const learnEdu = sectionMap['education-learning-approach-shell'] || sectionMap['education-learning-approach'] || {};
                const assessEdu = sectionMap['education-assessment-system-shell'] || sectionMap['education-assessment-system'] || {};
                const specEdu = sectionMap['education-special-programs'] || sectionMap['education-special-programs-shell'] || {};

                const transformedEdu = {
                  ...mainEdu,
                  ...mergedContent?.en,
                  kicker: heroEdu.kicker || mainEdu.kicker,
                  title: heroEdu.title || mainEdu.title,
                  summary: heroEdu.summary || mainEdu.summary,
                  hero_slides: heroEdu.slides ? heroEdu.slides.map(s => s.image || s.url || s) : mainEdu.hero_slides,
                  levels_kicker: levelsEdu.kicker || levelsEdu.badge || mainEdu.levels_kicker,
                  levels_title: levelsEdu.title || mainEdu.levels_title,
                  levels_subtitle: levelsEdu.subtitle || levelsEdu.description || mainEdu.levels_subtitle,
                  program_levels: Array.isArray(levelsEdu.levels)
                    ? levelsEdu.levels.filter(lvl => {
                        if (!lvl || typeof lvl !== 'object') return false;
                        if (lvl.delete !== undefined || lvl.deleted === true || lvl._deleted === true) return false;
                        const title = (lvl.title || '').trim().toLowerCase();
                        if (!title || title === 'xxx') return false;
                        return true;
                      })
                    : mainEdu.program_levels,
                  curriculum_overview: (currEdu.pillars || currEdu.tracks || currEdu.title || currEdu.summary) ? {
                    ...currEdu,
                    kicker: currEdu.kicker || mainEdu.curriculum_overview?.kicker || 'Curriculum Overview',
                    title: currEdu.title || mainEdu.curriculum_overview?.title,
                    summary: currEdu.summary || mainEdu.curriculum_overview?.summary,
                    note: currEdu.note || mainEdu.curriculum_overview?.note,
                    pillars: Array.isArray(currEdu.pillars)
                      ? currEdu.pillars.filter(p => p && typeof p === 'object' && p.delete === undefined && !p.deleted && !p._deleted && (p.title || '').trim() && (p.title || '').trim().toLowerCase() !== 'xxx')
                      : (mainEdu.curriculum_overview?.pillars || []),
                    tracks: Array.isArray(currEdu.tracks)
                      ? currEdu.tracks.filter(t => t && typeof t === 'object' && t.delete === undefined && !t.deleted && !t._deleted && (t.title || '').trim() && (t.title || '').trim().toLowerCase() !== 'xxx')
                      : (mainEdu.curriculum_overview?.tracks || [])
                  } : mainEdu.curriculum_overview,
                  subjects_kicker: subjEdu.kicker || (subjEdu.subjects_offered && subjEdu.subjects_offered.kicker) || 'Subjects Offered',
                  subjects_title: subjEdu.title || (subjEdu.subjects_offered && subjEdu.subjects_offered.title) || 'Balanced Academics, Creativity, and Character Growth',
                  subjects_subtitle: subjEdu.subtitle || (subjEdu.subjects_offered && subjEdu.subjects_offered.subtitle) || 'A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners.',
                  subjects_description: subjEdu.description || (subjEdu.subjects_offered && subjEdu.subjects_offered.description) || 'A well-rounded subject mix that strengthens mastery, nurtures curiosity, and shapes students into confident learners.',
                  subjects_stats: Array.isArray(subjEdu.stats)
                    ? subjEdu.stats
                    : (Array.isArray(subjEdu.subjects_offered?.stats) ? subjEdu.subjects_offered.stats : []),
                  subjects_offered: Array.isArray(subjEdu.subjects)
                    ? subjEdu.subjects.filter(s => s && (typeof s === 'string' || (typeof s === 'object' && s.delete === undefined && !s.deleted && !s._deleted && (s.name || s.title || '').trim())))
                    : (Array.isArray(subjEdu.subjects_offered?.subjects)
                      ? subjEdu.subjects_offered.subjects.filter(s => s && (typeof s === 'string' || (typeof s === 'object' && s.delete === undefined && !s.deleted && !s._deleted && (s.name || s.title || '').trim())))
                      : (Array.isArray(subjEdu.subjects_offered)
                        ? subjEdu.subjects_offered.filter(s => s && (typeof s === 'string' || (typeof s === 'object' && s.delete === undefined && !s.deleted && !s._deleted && (s.name || s.title || '').trim())))
                        : mainEdu.subjects_offered)),
                  learning_kicker: learnEdu.kicker || 'Learning Approach',
                  learning_title: learnEdu.title || '5E Model, Inquiry, Project & Problem-Based Learning',
                  learning_subtitle: learnEdu.subtitle || 'A comprehensive framework that develops curiosity, collaboration, and mastery across every level.',
                  learning_description: learnEdu.description || '',
                  learning_approach: (learnEdu.pedagogical_model || learnEdu.pedagogicalModel || learnEdu.title || learnEdu.signature_strategies || learnEdu.signatureStrategies || learnEdu.integration) ? {
                    ...learnEdu,
                    kicker: learnEdu.kicker || 'Learning Approach',
                    title: learnEdu.title || mainEdu.learning_approach?.title || '5E Model, Inquiry, Project & Problem-Based Learning',
                    subtitle: learnEdu.subtitle || mainEdu.learning_approach?.subtitle || 'A comprehensive framework that develops curiosity, collaboration, and mastery across every level.',
                    description: learnEdu.description || mainEdu.learning_approach?.description || '',
                    pedagogicalModel: learnEdu.pedagogical_model || learnEdu.pedagogicalModel || mainEdu.learning_approach?.pedagogicalModel,
                    signatureStrategies: Array.isArray(learnEdu.signature_strategies)
                      ? learnEdu.signature_strategies.filter(s => s && (typeof s === 'string' || (typeof s === 'object' && s.delete === undefined && !s.deleted && !s._deleted && (s.title || s.name || '').trim())))
                      : (Array.isArray(learnEdu.signatureStrategies)
                        ? learnEdu.signatureStrategies.filter(s => s && (typeof s === 'string' || (typeof s === 'object' && s.delete === undefined && !s.deleted && !s._deleted && (s.title || s.name || '').trim())))
                        : (mainEdu.learning_approach?.signatureStrategies || [])),
                    integration: learnEdu.integration || mainEdu.learning_approach?.integration
                  } : mainEdu.learning_approach,
                  assessment_system: (assessEdu.points || assessEdu.title || assessEdu.summary || assessEdu.methodology || assessEdu.what_this_means || assessEdu.assessment_calendar || assessEdu.balanced_approach_title) ? {
                    ...mainEdu.assessment_system,
                    ...assessEdu,
                    kicker: assessEdu.kicker || mainEdu.assessment_system?.kicker || 'Assessment System',
                    title: assessEdu.title || mainEdu.assessment_system?.title || "How students' progress is evaluated",
                    summary: assessEdu.summary || mainEdu.assessment_system?.summary || 'We use a balanced assessment system that values daily growth, authentic performance, and mastery of standards.',
                    balanced_approach_title: assessEdu.balanced_approach_title || mainEdu.assessment_system?.balanced_approach_title || 'Our Balanced Approach',
                    balanced_approach_description: assessEdu.balanced_approach_description || mainEdu.assessment_system?.balanced_approach_description,
                    assessment_methodology_title: assessEdu.assessment_methodology_title || mainEdu.assessment_system?.assessment_methodology_title || 'Our Assessment Methodology',
                    assessment_methodology_subtitle: assessEdu.assessment_methodology_subtitle || mainEdu.assessment_system?.assessment_methodology_subtitle || 'Four pillars that guide how we evaluate and support student learning',
                    assessment_calendar_header_title: assessEdu.assessment_calendar_header_title || mainEdu.assessment_system?.assessment_calendar_header_title || 'Assessment Calendar',
                    assessment_calendar_header_subtitle: assessEdu.assessment_calendar_header_subtitle || mainEdu.assessment_system?.assessment_calendar_header_subtitle || 'Key assessment touchpoints throughout the academic year',
                    points: Array.isArray(assessEdu.points)
                      ? assessEdu.points.filter(pt => pt && (typeof pt === 'string' || (typeof pt === 'object' && pt.delete === undefined && !pt.deleted && !pt._deleted && (pt.title || pt.name || '').trim())))
                      : mainEdu.assessment_system?.points,
                    methodology: Array.isArray(assessEdu.methodology)
                      ? assessEdu.methodology.filter(m => m && (typeof m === 'string' || (typeof m === 'object' && m.delete === undefined && !m.deleted && !m._deleted && (m.title || m.phase || m.name || '').trim())))
                      : mainEdu.assessment_system?.methodology,
                    what_this_means: Array.isArray(assessEdu.what_this_means)
                      ? assessEdu.what_this_means.filter(w => w && (typeof w === 'string' || (typeof w === 'object' && w.delete === undefined && !w.deleted && !w._deleted && (w.title || '').trim())))
                      : mainEdu.assessment_system?.what_this_means,
                    assessment_calendar: Array.isArray(assessEdu.assessment_calendar)
                      ? assessEdu.assessment_calendar.filter(c => c && (typeof c === 'string' || (typeof c === 'object' && c.delete === undefined && !c.deleted && !c._deleted && (c.title || c.period || '').trim())))
                      : mainEdu.assessment_system?.assessment_calendar,
                  } : mainEdu.assessment_system,
                  special_programs: Array.isArray(specEdu.programs) && specEdu.programs.length > 0
                    ? specEdu.programs.filter(p => p && (typeof p === 'string' || (typeof p === 'object' && p.delete === undefined && !p.deleted && !p._deleted && (p.title || '').trim())))
                    : mainEdu.special_programs,
                  special_programs_shell: {
                    kicker: specEdu.kicker,
                    title: specEdu.title,
                    subtitle: specEdu.subtitle,
                    description: specEdu.description,
                    note: specEdu.note,
                    stats: specEdu.stats
                  }
                };
                pageData = merge(transformedEdu);
              } else if (cmsSlug === 'admissions') {
                const mainAdm = sectionMap['admissions'] || {};
                const heroAdm = sectionMap['admissions-hero'] || {};
                const levelsAdm = sectionMap['admissions-levels'] || {};
                const procAdm = sectionMap['admissions-process'] || {};
                const reqAdm = sectionMap['admissions-requirements'] || {};
                const datesAdm = sectionMap['admissions-key-dates'] || {};
                const feesAdm = sectionMap['admissions-fees-payment'] || {};
                const onlineAdm = sectionMap['admissions-online-application'] || {};

                pageData = {
                  ...mainAdm,
                  ...mergedContent?.en,
                  sections: sectionMap,
                  raw_sections: sectionMap,
                  process: procAdm,
                  title: heroAdm.title || mainAdm.title,
                  summary: heroAdm.subtitle || heroAdm.summary || mainAdm.summary,
                  hero_kicker: heroAdm.kicker || mainAdm.kicker || 'Admissions',
                  hero_slides: heroAdm.slides ? heroAdm.slides.map(s => s.image || s.url || s) : (mainAdm.hero_slides || mainAdm.slides),
                  hero_video: heroAdm.video || heroAdm.video_url || heroAdm.video_file || mainAdm.hero_video,
                  admissions_levels: levelsAdm.levels || levelsAdm.admissions_levels || mainAdm.admissions_levels,
                  levels: levelsAdm.levels || levelsAdm.admissions_levels || mainAdm.admissions_levels,
                  key_dates: datesAdm.dates || datesAdm.keyDates || datesAdm.key_dates || mainAdm.key_dates,
                  fees_payment: feesAdm.blocks || feesAdm.feesPayment || feesAdm.fees_payment || mainAdm.fees_payment,
                  online_application: (onlineAdm.title || onlineAdm.ctaLabel || onlineAdm.summary) ? onlineAdm : mainAdm.online_application,
                  process_steps: procAdm.steps || procAdm.process_steps || mainAdm.process_steps || mainAdm.steps,
                  steps: procAdm.steps || procAdm.process_steps || mainAdm.steps || mainAdm.process_steps,
                  requirements: reqAdm.requirements || reqAdm.requirementsByLevel || mainAdm.requirements
                };
              } else if (cmsSlug === 'secondary') {
                const { mergeSecondaryData } = await import('./data/secondaryDefaults');
                pageData = mergeSecondaryData(mergedContent?.en || {});
              } else if (cmsSlug === 'student-life') {
                const studentLifeSections = {};
                pageSections.forEach(section => {
                  const cp = (section.content_published && section.content_published !== '{}' && section.content_published !== '[]') 
                    ? section.content_published 
                    : (section.content || section.content_published);
                  let parsed = null;
                  try {
                    parsed = typeof cp === 'string' ? JSON.parse(cp) : cp;
                  } catch (e) {
                    parsed = cp;
                  }
                  const st = section.section_type;
                  if (parsed) {
                    const contentEn = parsed.en || parsed;
                    if (!studentLifeSections[st]) {
                      studentLifeSections[st] = contentEn;
                    } else {
                      const existing = studentLifeSections[st];
                      const merged = { ...existing };
                      for (const [k, v] of Object.entries(contentEn)) {
                        if (Array.isArray(v)) {
                          if (v.length > 0 || !Array.isArray(existing[k]) || existing[k].length === 0) {
                            merged[k] = v;
                          }
                        } else if (v !== null && v !== undefined && v !== '') {
                          merged[k] = v;
                        }
                      }
                      studentLifeSections[st] = merged;
                    }
                  }
                });
                pageData = {
                  ...mergedContent?.en,
                  sections: studentLifeSections,
                  raw_sections: studentLifeSections,
                  hero: studentLifeSections['student-life-hero'],
                  clubs: studentLifeSections['student-life-clubs']?.clubs || mergedContent?.en?.clubs,
                  events: studentLifeSections['student-life-events']?.events || mergedContent?.en?.events,
                  houses: studentLifeSections['student-life-house-system']?.houses || mergedContent?.en?.houses,
                  houseSystem: studentLifeSections['student-life-house-system'],
                  characterShaping: studentLifeSections['student-life-character-shaping'],
                  leaps: studentLifeSections['student-life-leaps'],
                  wellbeing: studentLifeSections['student-life-wellbeing']
                };
              } else if (cmsSlug === 'facilities') {
                const heroSec = sectionMap['facilities-hero'] || sectionMap['hero'] || {};
                const overviewSec = sectionMap['facilities-overview'] || sectionMap['facilities-campus-overview'] || {};
                const techSec = sectionMap['facilities-technology'] || {};
                const safetySec = sectionMap['facilities-safety'] || {};
                const gallerySec = sectionMap['facilities-gallery'] || {};
                const tourSec = sectionMap['facilities-tour'] || sectionMap['facilities-virtual-tour'] || {};

                pageData = {
                  ...mergedContent?.en,
                  sections: sectionMap,
                  title: heroSec.title || '',
                  summary: heroSec.summary || heroSec.subtitle || '',
                  slides: heroSec.slides || heroSec.hero_slides || [],
                  hero_slides: heroSec.slides ? heroSec.slides.map(s => (s && typeof s === 'object') ? (s.image || s.url || s) : s) : (heroSec.hero_slides || []),

                  overview_kicker: overviewSec.kicker || '',
                  overview_title: overviewSec.title || '',
                  overview_description: overviewSec.description || '',
                  campus_overview: overviewSec.items || overviewSec.campus_overview || [],

                  tech_kicker: techSec.kicker || '',
                  tech_title: techSec.title || '',
                  tech_description: techSec.description || '',
                  technology_innovation: techSec.items || techSec.technology_innovation || [],

                  safety_kicker: safetySec.kicker || '',
                  safety_title: safetySec.title || '',
                  safety_description: safetySec.description || '',
                  safety_security: safetySec.items || safetySec.safety_security || [],

                  gallery_kicker: gallerySec.kicker || '',
                  gallery_title: gallerySec.title || '',
                  gallery_description: gallerySec.description || '',
                  photo_gallery: gallerySec.items || gallerySec.photo_gallery || [],

                  virtual_tour: (tourSec.title || tourSec.summary || tourSec.ctaLabel || tourSec.image) ? {
                    kicker: tourSec.kicker || '',
                    title: tourSec.title || '',
                    summary: tourSec.summary || '',
                    ctaLabel: tourSec.ctaLabel || '',
                    ctaHref: tourSec.ctaHref || '#',
                    image: tourSec.image || ''
                  } : undefined
                };
              } else if (cmsSlug === 'news-and-updates' || cmsSlug === 'news-updates') {
                const heroSec = sectionMap['news-hero'] || sectionMap['news-updates-hero'] || sectionMap['hero'] || {};
                const pressSec = sectionMap['news-press'] || sectionMap['news-press-releases'] || {};
                const articlesSec = sectionMap['news-articles'] || sectionMap['news-blog-articles'] || sectionMap['news-list'] || {};

                pageData = {
                  ...mergedContent?.en,
                  kicker: heroSec.kicker || 'News & Updates',
                  title: heroSec.title || 'News & Updates',
                  summary: heroSec.summary || heroSec.subtitle || 'Stay informed with the latest announcements, campus events, and development updates.',
                  slides: heroSec.slides || heroSec.hero_slides,
                  hero_slides: heroSec.slides ? heroSec.slides.map(s => s.image || s.url || s) : (heroSec.hero_slides || undefined),

                  press_releases_kicker: pressSec.kicker || 'Press Releases',
                  press_releases_title: pressSec.title || 'Community Involvement & Partnerships',
                  press_releases_description: pressSec.description,
                  press_releases: pressSec.items || pressSec.press_releases,

                  blog_articles_kicker: articlesSec.kicker || 'Blog / Articles',
                  blog_articles_title: articlesSec.title || 'Education Trends, Student Highlights & Insights',
                  blog_articles_description: articlesSec.description,
                  blog_articles: articlesSec.items || articlesSec.blog_articles || articlesSec.articles
                };
              } else if (cmsSlug === 'parents') {
                const heroSec = sectionMap['parents-hero'] || sectionMap['hero'] || {};
                const palcSec = sectionMap['parents-palc'] || sectionMap['parents-learning-community'] || sectionMap['palc'] || {};
                const handbookSec = sectionMap['parents-handbook'] || sectionMap['parent-student-handbook'] || sectionMap['handbook'] || {};
                const portalSec = sectionMap['parents-portal'] || sectionMap['parent-portal-access'] || sectionMap['portal'] || {};
                const commSec = sectionMap['parents-communication'] || sectionMap['communication-channels'] || sectionMap['communication'] || {};

                pageData = {
                  ...mergedContent?.en,
                  kicker: heroSec.kicker || 'Parents',
                  title: heroSec.title || 'Parents',
                  summary: heroSec.summary || heroSec.subtitle || 'Strengthening the partnership between families and school through meaningful collaboration.',
                  slides: heroSec.slides || heroSec.hero_slides,
                  hero_slides: heroSec.slides ? heroSec.slides.map(s => s.image || s.url || s) : (heroSec.hero_slides || undefined),

                  palc_kicker: palcSec.kicker || 'Parents Learning Community',
                  palc_title: palcSec.title || 'Growing Together Through Shared Learning & Support',
                  palc_subtitle: palcSec.subtitle || palcSec.description,
                  palc_stats: palcSec.stats,
                  parents_learning_community: palcSec.items || palcSec.parents_learning_community,
                  palc_benefits: palcSec.benefits,

                  handbook_kicker: handbookSec.kicker || 'Parent & Student Handbook',
                  handbook_title: handbookSec.title || 'Essential Guide to School Life',
                  handbook_subtitle: handbookSec.subtitle,
                  handbook: handbookSec.handbook,
                  inside_topics: handbookSec.inside_topics,

                  portal_kicker: portalSec.kicker || 'Parent Portal Access',
                  portal_title: portalSec.title || 'Stay Connected & Informed',
                  portal_subtitle: portalSec.subtitle,
                  portal_features: portalSec.features,
                  portal_cta: portalSec.cta,

                  communication_kicker: commSec.kicker || 'Communication Channels',
                  communication_title: commSec.title || 'Multiple Ways to Stay Connected',
                  communication_subtitle: commSec.subtitle,
                  communication_channels: commSec.channels
                };
              } else if (cmsSlug === 'careers') {
                const heroSec = sectionMap['careers-hero'] || sectionMap['hero'] || {};
                const philSec = sectionMap['careers-philosophy'] || sectionMap['philosophy'] || sectionMap['join-our-team'] || {};
                const posSec = sectionMap['careers-positions'] || sectionMap['careers-open-positions'] || sectionMap['open-positions'] || {};
                const oppSec = sectionMap['careers-opportunities'] || sectionMap['internships-volunteering'] || {};

                pageData = {
                  ...mergedContent?.en,
                  kicker: heroSec.kicker || 'Careers',
                  title: heroSec.title || 'Careers',
                  summary: heroSec.summary || heroSec.subtitle || 'Join our mission to nurture learners with character, competence, and compassion.',
                  slides: heroSec.slides || heroSec.hero_slides,
                  hero_slides: heroSec.slides ? heroSec.slides.map(s => s.image || s.url || s) : (heroSec.hero_slides || undefined),

                  philosophy_kicker: philSec.kicker || 'Join Our Team',
                  philosophy_title: philSec.title || 'Philosophy of Hiring and Development',
                  philosophy_description: philSec.description,
                  hiring_philosophy: philSec.items || philSec.hiring_philosophy,
                  philosophy_cta: philSec.cta,

                  positions_kicker: posSec.kicker || 'Open Positions',
                  positions_title: posSec.title || 'Join Our Growing Team',
                  positions_description: posSec.description,
                  open_positions: posSec.items || posSec.open_positions,

                  opportunities_kicker: oppSec.kicker || 'Internships / Volunteering',
                  opportunities_title: oppSec.title || 'Opportunities to Learn, Serve, and Grow',
                  opportunities_description: oppSec.description,
                  internships_volunteering: oppSec.items || oppSec.internships_volunteering
                };
              } else if (cmsSlug === 'contact-us' || cmsSlug === 'contact') {
                const heroSec = sectionMap['contact-hero'] || sectionMap['hero'] || {};
                const infoSec = sectionMap['contact-info'] || sectionMap['contact-location'] || sectionMap['contact-details'] || sectionMap['contact-form'] || {};
                const faqSec = sectionMap['contact-faq'] || sectionMap['faq'] || {};
                const partSec = sectionMap['contact-partnerships'] || sectionMap['partnerships'] || {};
                const testSec = sectionMap['contact-testimonials'] || sectionMap['testimonials'] || {};
                const countSec = sectionMap['contact-countdown'] || sectionMap['countdown'] || sectionMap['contact-construction'] || sectionMap['construction'] || {};

                pageData = {
                  ...mergedContent?.en,
                  kicker: heroSec.kicker || 'Contact Us',
                  title: heroSec.title || 'Contact Us',
                  summary: heroSec.summary || heroSec.subtitle || 'We are here to help. Reach out for admissions support, campus visits, or general enquiries.',
                  slides: heroSec.slides || heroSec.hero_slides,
                  hero_slides: heroSec.slides ? heroSec.slides.map(s => s.image || s.url || s) : (heroSec.hero_slides || undefined),

                  form_kicker: infoSec.form_kicker || 'Contact Form',
                  form_title: infoSec.form_title || 'Send us a message',
                  form_description: infoSec.form_description || 'Share your questions and our team will respond as soon as possible.',
                  whatsapp_number: infoSec.whatsapp_number || '08815130338',
                  location_kicker: infoSec.location_kicker || 'Get In Touch',
                  location_title: infoSec.location_title || 'Visit Us Today',
                  location_description: infoSec.location_description || 'Located in the heart of Jakarta with easy access and a welcoming environment for families.',
                  map_embed_url: cleanGoogleMapsEmbedUrl(infoSec.map_embed_url, infoSec.address),
                  address: infoSec.address || 'Jl. Raya Permata Harapan No. 10, Jakarta Timur 13220',
                  map_directions_url: infoSec.map_directions_url || 'https://maps.google.com/?q=Permata%20Harapan%20School%20Jakarta',
                  phone: infoSec.phone || '+62 21 555 1234',
                  email: infoSec.email || 'info@permataharapan.sch.id',
                  office_hours: (infoSec.office_hours_weekday && infoSec.office_hours_weekend)
                    ? [infoSec.office_hours_weekday, infoSec.office_hours_weekend]
                    : (infoSec.office_hours || ['Mon - Fri: 07:30 - 16:00', 'Sat: 08:00 - 12:00']),
                  social_instagram: infoSec.social_instagram || 'https://instagram.com/permataharapan',
                  social_facebook: infoSec.social_facebook || 'https://facebook.com/permataharapan',
                  social_youtube: infoSec.social_youtube || 'https://youtube.com/@permataharapan',
                  social_linkedin: infoSec.social_linkedin || 'https://linkedin.com/school/permataharapan',

                  faq_kicker: faqSec.kicker || 'FAQ',
                  faq_title: faqSec.title || 'Quick answers about school life',
                  faq_description: faqSec.description || 'Get fast answers about curriculum, uniforms, school hours, and more.',
                  faqs: faqSec.items || faqSec.faqs,

                  partnerships_kicker: partSec.kicker || 'Partnerships / Affiliations',
                  partnerships_title: partSec.title || 'Universities, organizations, and education networks',
                  partnerships_description: partSec.description,
                  partnerships: partSec.items || partSec.partnerships,

                  testimonials_kicker: testSec.kicker || 'Testimonials',
                  testimonials_title: testSec.title || 'Stories that shape our community',
                  testimonials_description: testSec.description,
                  testimonials: testSec.items || testSec.testimonials,
                  submit_tip_title: testSec.submit_tip_title || 'Submit Testimonial',
                  submit_tip_subtitle: testSec.submit_tip_subtitle || 'Help families discover Permata Harapan',
                  submit_tips: testSec.submit_tips,

                  countdown_kicker: countSec.kicker || 'Construction Updates',
                  countdown_title: countSec.title || 'Our Campus Opens in August 2026!',
                  countdown_description: countSec.description || 'Follow our progress as we build the next chapter for learning.',
                  target_date: countSec.target_date || '2026-08-01T00:00:00',
                  construction_image: countSec.construction_image || '/assets/news/news5.jpg',
                  construction_progress: countSec.construction_progress !== undefined ? countSec.construction_progress : 72,
                  construction_status: countSec.construction_status || 'Structural work completed. Interior finishing and landscape underway.',
                  construction_next_milestone: countSec.construction_next_milestone || 'Classroom setup, lab installations, and safety certifications.',
                  construction_milestones: countSec.construction_milestones || [
                    'Foundation and structural frame completed',
                    'Interior finishing in progress',
                    'Lab installations scheduled',
                    'Landscape and safety certification next'
                  ]
                };
              } else {
                pageData = mergedContent?.en || {};
              }
              // Set real data from database
              console.log(`[App] Setting page with database data`);
              pageData._slug = cmsSlug;
              try {
                sessionStorage.setItem('cms_page_' + cmsSlug, JSON.stringify(pageData));
              } catch (e) {}
              setPage(pageData);
              setPageSource({ ...pageData, section_type: cmsSlug });
              setLoadingPage(false);
            } else {
              // No data in DB yet - show empty/default state
              console.warn(`[App] No sections found for page ${cmsSlug}. Using defaults.`);
              setLoadingPage(false);
              let fallbackData = {};
              if (cmsSlug === 'about-us') {
                fallbackData = mergeAboutUsData({});
              } else if (cmsSlug === 'education-program') {
                const { mergeEducationData: merge } = await import('./data/educationDefaults');
                fallbackData = merge({});
              } else if (cmsSlug === 'secondary') {
                const { mergeSecondaryData } = await import('./data/secondaryDefaults');
                fallbackData = mergeSecondaryData({});
              } else if (cmsSlug === 'admissions') {
                fallbackData = { ...ADMISSIONS_DEFAULTS };
              } else if (cmsSlug === 'student-life') {
                fallbackData = {};
              } else if (cmsSlug === 'facilities' || cmsSlug === 'news-and-updates' || cmsSlug === 'news-updates' || cmsSlug === 'parents' || cmsSlug === 'careers' || cmsSlug === 'contact-us' || cmsSlug === 'contact') {
                fallbackData = {};
              }
              fallbackData._slug = cmsSlug;
              setPage(fallbackData);
            }
          } else {
            // No response or empty sections - fallback to defaults
            console.error(`[App] No sections data in response for ${cmsSlug}`);
            setLoadingPage(false);
            let fallbackData = {};
            if (cmsSlug === 'about-us') {
              fallbackData = mergeAboutUsData({});
            } else if (cmsSlug === 'education-program') {
              const { mergeEducationData: merge } = await import('./data/educationDefaults');
              fallbackData = merge({});
            } else if (cmsSlug === 'secondary') {
              const { mergeSecondaryData } = await import('./data/secondaryDefaults');
              fallbackData = mergeSecondaryData({});
            } else if (cmsSlug === 'admissions') {
              fallbackData = { ...ADMISSIONS_DEFAULTS };
            } else if (cmsSlug === 'student-life') {
              fallbackData = {};
            } else if (cmsSlug === 'facilities' || cmsSlug === 'news-and-updates' || cmsSlug === 'news-updates' || cmsSlug === 'parents' || cmsSlug === 'careers' || cmsSlug === 'contact-us' || cmsSlug === 'contact') {
              fallbackData = {};
            }
            fallbackData._slug = cmsSlug;
            setPage(fallbackData);
          }
        } catch (err) {
          // Fetch failed - fallback to default/hardcoded content instead of showing error
          console.error('[App] CMS fetch failed:', err.message);
          console.log('[App] Using hardcoded defaults as fallback');
          setLoadingPage(false);
          setError(''); // Clear error - use defaults instead
          
          let fallbackData = {};
          // Set default content based on page type
          if (cmsSlug === 'about-us') {
            fallbackData = mergeAboutUsData({});
          } else if (cmsSlug === 'education-program') {
            const { mergeEducationData: merge } = await import('./data/educationDefaults');
            fallbackData = merge({});
          } else if (cmsSlug === 'secondary') {
            const { mergeSecondaryData } = await import('./data/secondaryDefaults');
            fallbackData = mergeSecondaryData({});
          } else if (cmsSlug === 'admissions') {
            fallbackData = { ...ADMISSIONS_DEFAULTS };
          } else if (cmsSlug === 'student-life') {
            fallbackData = {};
          } else if (cmsSlug === 'facilities' || cmsSlug === 'news-and-updates' || cmsSlug === 'news-updates' || cmsSlug === 'parents' || cmsSlug === 'careers' || cmsSlug === 'contact-us' || cmsSlug === 'contact') {
            fallbackData = {};
          }
          fallbackData._slug = cmsSlug;
          setPage(fallbackData);
        }
        return;
      }

      const isCustomDetail = 
        activeSlug.startsWith('news-blog-') ||
        activeSlug.startsWith('news-press-') ||
        activeSlug.startsWith('news/') ||
        activeSlug.startsWith('facility-') ||
        activeSlug.startsWith('technology-') ||
        activeSlug.startsWith('house-') ||
        activeSlug.startsWith('palc-');

      if (staticPages.includes(activeSlug) || isCustomDetail || isDashboardView || isDashboardPath) {
        setLoadingPage(false);
        setPageSource(null);
        return;
      }

      setLoadingPage(true);
      try {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), 8000);
        const response = await fetch(`${API_BASE}/pages/${activeSlug}`, { signal: controller.signal });
        clearTimeout(t);
        const payload = await response.json();
        if (!response.ok || !payload.ok) {
          throw new Error(payload.message || 'Failed to load page');
        }
        setPageSource(payload.data);
        setPage(payload.data);
      } catch (err) {
        setError(err.message || 'Page request failed');
      } finally {
        setLoadingPage(false);
      }
    }

    loadPage();
  }, [activeSlug, pageRefreshCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    async function translatePage() {
      if (!pageSource || activeSlug === 'homepage' || activeSlug === 'about-us' || activeSlug === 'education-program') {
        return;
      }
      if (language === 'en') {
        setPage(pageSource);
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/translate-page`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'en',
            target: 'id',
            data: pageSource,
          }),
        });
        const payload = await response.json();
        if (!response.ok || !payload.ok) {
          throw new Error(payload.message || 'Translate request failed');
        }
        setPage(payload.data);
      } catch (err) {
        setError(err.message || 'Translate request failed');
        setPage(pageSource);
      }
    }

    translatePage();
  }, [language, pageSource, activeSlug, pageRefreshCounter]);

  useEffect(() => {
    if (!pendingAnchor || loadingPage) {
      return;
    }

    const target = document.getElementById(pendingAnchor);
    const header = document.querySelector('.top-header');
    const headerOffset = header?.getBoundingClientRect().height || 0;
    const scrollToTarget = (node) => {
      const targetTop = node.getBoundingClientRect().top + window.pageYOffset;
      const offsetTop = Math.max(targetTop - headerOffset - 30, 0);
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    };
    if (target) {
      scrollToTarget(target);
      setPendingAnchor('');
      return;
    }

    const timeoutId = setTimeout(() => {
      const retryTarget = document.getElementById(pendingAnchor);
      if (retryTarget) {
        scrollToTarget(retryTarget);
      }
      setPendingAnchor('');
    }, 120);

    return () => clearTimeout(timeoutId);
  }, [pendingAnchor, loadingPage]);

  useEffect(() => {
    if ((activeSlug === 'homepage' || activeSlug.startsWith('house-')) && !pendingAnchor) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [activeSlug, pendingAnchor]);

  useEffect(() => {
    if (!menu.length || activeSlug === 'homepage' || activeItemId !== null) {
      return;
    }

    for (const item of menu) {
      const itemKey = item.content_slug || item.slug;
      if (itemKey === activeSlug) {
        setActiveItemId(item.id);
        return;
      }

      if (item.children && item.children.length > 0) {
        const matchedChild = item.children.find(
          (child) => (child.content_slug || child.slug) === activeSlug
        );
        if (matchedChild) {
          setActiveItemId(matchedChild.id);
          return;
        }
      }
    }
  }, [menu, activeSlug, activeItemId]);

  function onSelectSlug(item) {
    const nextSlug = item.content_slug || item.slug;
    setError('');
    if (['about-us', 'education-program', 'secondary', 'admissions', 'student-life', 'facilities', 'news-updates', 'parents', 'careers', 'contact-us'].includes(nextSlug)) {
      let foundCache = false;
      try {
        const cached = sessionStorage.getItem('cms_page_' + nextSlug);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed._slug === nextSlug) {
            setPage(parsed);
            setLoadingPage(false);
            foundCache = true;
          }
        }
      } catch (e) {}
      if (!foundCache) {
        setLoadingPage(true);
        setPage(prev => (prev && prev._slug === nextSlug ? prev : null));
      }
    }
    setActiveSlug(nextSlug);
    setActiveItemId(item.id);
    setPageRefreshCounter(prev => prev + 1);
  }

  function goToSection(slug, anchor) {
    setError('');
    if (['about-us', 'education-program', 'secondary', 'admissions', 'student-life', 'facilities', 'news-updates', 'parents', 'careers', 'contact-us'].includes(slug)) {
      let foundCache = false;
      try {
        const cached = sessionStorage.getItem('cms_page_' + slug);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed._slug === slug) {
            setPage(parsed);
            setLoadingPage(false);
            foundCache = true;
          }
        }
      } catch (e) {}
      if (!foundCache) {
        setLoadingPage(true);
        setPage(prev => (prev && prev._slug === slug ? prev : null));
      }
    }
    setActiveSlug(slug);
    setActiveItemId(null);
    setPendingAnchor(anchor);
    setPageRefreshCounter(prev => prev + 1);
  }

  function goToSlug(slug) {
    setError('');
    if (!slug) return;
    if (typeof slug === 'string' && slug.includes('#')) {
      const [s, a] = slug.split('#');
      goToSection(s, a);
      return;
    }
    if (slug === 'login') {
      if (window.location.pathname !== '/login') {
        window.history.pushState({}, '', '/login');
      }
      setActiveSlug('login');
      setActiveItemId(null);
      return;
    }
    if (slug === 'register') {
      if (window.location.pathname !== '/register') {
        window.history.pushState({}, '', '/register');
      }
      setActiveSlug('register');
      setActiveItemId(null);
      return;
    }
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
    if (['about-us', 'education-program', 'secondary', 'admissions', 'student-life', 'facilities', 'news-and-updates', 'news-updates', 'parents', 'careers', 'contact-us'].includes(slug)) {
      let foundCache = false;
      try {
        const cached = sessionStorage.getItem('cms_page_' + slug);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed._slug === slug) {
            setPage(parsed);
            setLoadingPage(false);
            foundCache = true;
          }
        }
      } catch (e) {}
      if (!foundCache) {
        setLoadingPage(true);
        setPage(prev => (prev && prev._slug === slug ? prev : null));
      }
    }
    setActiveSlug(slug);
    setActiveItemId(null);
    setPageRefreshCounter(prev => prev + 1);
  }

  function goToHomepage() {
    setError('');
    setPage(null);
    setLoadingPage(false);
    setActiveSlug('homepage');
    setActiveItemId(null);
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/');
    }
  }

  function findActiveItemTitle(items, targetId) {
    for (const item of items) {
      if (item.id === targetId) {
        return item.title;
      }
      if (item.children && item.children.length > 0) {
        const matchedChild = item.children.find((child) => child.id === targetId);
        if (matchedChild) {
          return matchedChild.title;
        }
      }
    }
    return null;
  }

  function findMenuItemByTitle(items, keyword) {
    const normalizedKeyword = keyword.toLowerCase();
    for (const item of items) {
      if (String(item.title || '').toLowerCase().includes(normalizedKeyword)) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const childMatch = item.children.find((child) =>
          String(child.title || '').toLowerCase().includes(normalizedKeyword)
        );
        if (childMatch) {
          return childMatch;
        }
      }
    }
    return null;
  }

  function getFallbackSubmenu(item) {
    const slug = item?.content_slug || item?.slug;
    const title = String(item?.title || '').toLowerCase();
    const fallbackMap = {
      'about-us': [
        
        { title: 'Welcome Message', slug: 'about-us', anchor: 'welcome-message' },
        { title: 'Vision, Mission & Values', slug: 'about-us', anchor: 'vision-mission-values' },
        { title: 'School Philosophy', slug: 'about-us', anchor: 'school-philosophy' },
        { title: 'History', slug: 'about-us', anchor: 'our-history' },
        { title: 'Leadership', slug: 'about-us', anchor: 'school-leadership' },
      ],
      'education-program': [
        
        { title: 'Programs', slug: 'education-program', anchor: 'program-levels' },
        { title: 'Curriculum Overview', slug: 'education-program', anchor: 'curriculum-overview' },
        { title: 'Subjects Offered', slug: 'education-program', anchor: 'subjects-offered' },
        { title: 'Learning Approach', slug: 'education-program', anchor: 'learning-approach' },
        { title: 'Assessment System', slug: 'education-program', anchor: 'assessment-system' },
        { title: 'Special Programs', slug: 'education-program', anchor: 'special-programs' },
        { title: 'ECA & Talent Programs', slug: 'education-program', anchor: 'eca-programs' },
      ],
      admissions: [
        
        { title: 'Admissions Process', slug: 'admissions', anchor: 'admissions-process' },
        { title: 'Requirements', slug: 'admissions', anchor: 'admissions-requirements' },
        { title: 'Key Dates', slug: 'admissions', anchor: 'admissions-key-dates' },
        { title: 'Fees and Payment', slug: 'admissions', anchor: 'fees-payment' },
        { title: 'Online Application', slug: 'admissions', anchor: 'online-application' },
      ],
      'student-life': [
        
        { title: 'Clubs & ECA', slug: 'student-life', anchor: 'clubs-eca' },
        { title: 'Events & Competitions', slug: 'student-life', anchor: 'events-competitions' },
        { title: 'House System', slug: 'student-life', anchor: 'house-system' },
        { title: 'Wellbeing & SEL', slug: 'student-life', anchor: 'wellbeing-sel' },
        { title: 'Community Service', slug: 'student-life', anchor: 'community-service' },
      ],
      
      facilities: [
        
        { title: 'Campus Overview', slug: 'facilities', anchor: 'campus-overview' },
        { title: 'Technology & Innovation', slug: 'facilities', anchor: 'technology-innovation' },
        { title: 'Safety & Security', slug: 'facilities', anchor: 'safety-security' },
        { title: 'Photo Gallery / Virtual Tour', slug: 'facilities', anchor: 'photo-gallery' },
      ],
      'news-and-updates': [
        { title: 'Announcements', slug: 'news-and-updates', anchor: 'announcements' },
        { title: 'Press Releases', slug: 'news-and-updates', anchor: 'press-releases' },
        { title: 'Blog / Articles', slug: 'news-and-updates', anchor: 'blog-articles' },
      ],
      parents: [
        { title: 'Parents Learning Community (PaLC)', slug: 'parents', anchor: 'parents-learning-community' },
        { title: 'Parent&Student Handbook', slug: 'parents', anchor: 'parent-student-handbook' },
        { title: 'Parent Portal Access', slug: 'parents', anchor: 'parent-portal-access' },
        { title: 'Communication Channels', slug: 'parents', anchor: 'communication-channels' },
      ],
      careers: [
        { title: 'Join Our Team', slug: 'careers', anchor: 'join-our-team' },
        { title: 'Open Positions', slug: 'careers', anchor: 'open-positions' },
        { title: 'Internships / Volunteering', slug: 'careers', anchor: 'internships-volunteering' },
      ],
      'contact-us': [
        { title: 'Send Message / Inquire', slug: 'contact-us', anchor: 'contact-form' },
        { title: 'Location & Campus Map', slug: 'contact-us', anchor: 'contact-location' },
        { title: 'Frequently Asked Questions', slug: 'contact-us', anchor: 'contact-faq' },
        { title: 'Partnerships & Network', slug: 'contact-us', anchor: 'contact-partnerships' },
        { title: 'School Testimonials', slug: 'contact-us', anchor: 'contact-testimonials' },
        { title: 'Campus Construction Updates', slug: 'contact-us', anchor: 'contact-countdown' },
      ],
    };

    if (slug && fallbackMap[slug]) {
      return fallbackMap[slug];
    }

    if (slug === 'news-updates') {
      return fallbackMap['news-and-updates'];
    }

    if (slug === 'event-competitions') {
      return fallbackMap['events-competitions'];
    }

    if (slug === 'admissions' || title.includes('admission')) {
      return fallbackMap.admissions;
    }
    if (slug) {
      return [{ title: 'Overview', slug }];
    }
    return [];
  }

  function getSubmenuItemsForNavItem(item) {
    const itemSlug = item.content_slug || item.slug;
    const normalizedTitle = String(item.title || '').toLowerCase();
    const isAdmissionsMenu =
      itemSlug === 'admissions' || normalizedTitle.includes('admission');
    const isFacilitiesMenu =
      itemSlug === 'facilities' || normalizedTitle.includes('facilities');
    const isNewsUpdatesMenu =
      itemSlug === 'news-and-updates' ||
      itemSlug === 'news-updates' ||
      (normalizedTitle.includes('news') && normalizedTitle.includes('update'));
    const isParentsMenu = itemSlug === 'parents' || normalizedTitle.includes('parent');
    const isCareersMenu = itemSlug === 'careers' || normalizedTitle.includes('career');
    const fallbackSubmenu = getFallbackSubmenu(item);
    const isContactMenu = itemSlug === 'contact-us' || normalizedTitle.includes('contact');
    const preferFallback =
      isAdmissionsMenu ||
      isFacilitiesMenu ||
      isNewsUpdatesMenu ||
      isParentsMenu ||
      isCareersMenu ||
      isContactMenu ||
      [
        'about-us',
        'education-program',
        'student-life',
        'events-competitions',
        'event-competitions',
        'facilities',
        'news-and-updates',
        'parents',
        'careers',
        'contact-us',
      ].includes(itemSlug);

    if (preferFallback) {
      return fallbackSubmenu;
    }

    if (item.children && item.children.length > 0) {
      return item.children.map((child) => ({
        id: child.id,
        title: child.title,
        slug: child.content_slug || child.slug,
      }));
    }

    return fallbackSubmenu;
  }

  const isHomepage = activeSlug === 'homepage';
  const isAboutUs = activeSlug === 'about-us';
  const isTestContent = activeSlug === 'test-content';
  const isEducationProgram = activeSlug === 'education-program';
  const isAdmissions = activeSlug === 'admissions';
  const isStudentLife = activeSlug === 'student-life';
  const isFacilities = activeSlug === 'facilities';
  const isFacilityDetail = activeSlug.startsWith('facility-');
  const isTechnologyDetail = activeSlug.startsWith('technology-');
  const activeItemTitle = activeItemId ? findActiveItemTitle(menu, activeItemId) : null;
  const isParents = activeSlug === 'parents';
  const isPaLCDetail = activeSlug.startsWith('palc-');
  const isCareers = activeSlug === 'careers';
  const isContactUs = activeSlug === 'contact-us';
  const isRegister = activeSlug === 'register' || isRegisterPath;
  const isLogin = activeSlug === 'login' || isLoginPath;
  const isParentDashboard = activeSlug === 'parent-dashboard';
  const isStaffDashboard = activeSlug === 'staff-dashboard';
  const isNewsDetail = activeSlug.startsWith('news/');
  const isNewsPressDetail = activeSlug.startsWith('news-press-');
  const isNewsBlogDetail = activeSlug.startsWith('news-blog-');
  const isNewsAll = activeSlug === 'news-all';
  const isPreschool = activeSlug === 'preschool';
  const isKindergarten = activeSlug === 'kindergarten';
  const isPrimary = activeSlug === 'primary';
  const isSecondary = activeSlug === 'secondary';
  const isHouseDetail = activeSlug.startsWith('house-');
  const isNewsUpdatesByTitle =
    typeof activeItemTitle === 'string' &&
    activeItemTitle.toLowerCase().includes('news') &&
    activeItemTitle.toLowerCase().includes('update');
  const isNewsUpdates =
    activeSlug === 'news-and-updates' ||
    activeSlug === 'news-updates' ||
    isNewsUpdatesByTitle;
  const isEventsCompetitionsByTitle =
    typeof activeItemTitle === 'string' &&
    activeItemTitle.toLowerCase().includes('event') &&
    activeItemTitle.toLowerCase().includes('competition');
  const isEventsCompetitions =
    activeSlug === 'event-competitions' ||
    activeSlug === 'events-competitions' ||
    isEventsCompetitionsByTitle;
  const isDashboardView = isUnifiedGatePath || isDashboardPath || activeSlug === 'administrator' || activeSlug === 'director-dashboard' || activeSlug === 'principal-dashboard' || activeSlug === 'website-admin-cms' || isParentDashboard || isStaffDashboard;
    useEffect(() => {
    let pageName = '';
    if (isContactUs) pageName = 'Contact Us';
    else if (isAboutUs) pageName = 'About Us';
    else if (isEducationProgram) pageName = 'Education Program';
    else if (isAdmissions) pageName = 'Admissions';
    else if (isStudentLife) pageName = 'Student Life';
    else if (isFacilities) pageName = 'Facilities';
    else if (isNewsUpdates) pageName = 'News & Updates';
    else if (isParents) pageName = 'Parents';
    else if (isCareers) pageName = 'Careers';
    else if (activeItemTitle) pageName = activeItemTitle;

    document.title = pageName 
      ? `${pageName} | Permata Harapan Secondary`
      : 'Permata Harapan Secondary';
  }, [activeSlug, activeItemTitle, isContactUs, isAboutUs, isEducationProgram, isAdmissions, isStudentLife, isFacilities, isNewsUpdates, isParents, isCareers]);

  const activeMainClass = isDashboardView
    ? 'main-content main-content-dashboard'
    : `main-content ${isHomepage || isAboutUs || isEducationProgram || isAdmissions || isStudentLife || isEventsCompetitions || isFacilities || isFacilityDetail || isTechnologyDetail || isNewsUpdates || isNewsPressDetail || isNewsBlogDetail || isParents || isPaLCDetail || isCareers || isContactUs || isRegister || isLogin || isParentDashboard || isStaffDashboard || isNewsDetail || isNewsAll || isPreschool || isKindergarten || isPrimary || isSecondary || isHouseDetail ? 'main-content-home' : ''}`;
  const campusLinks = [
    { name: 'Campus 1', mapsUrl: 'https://maps.app.goo.gl/1bEGRUPtEwH8o1dV9' },
    { name: 'Campus 2', mapsUrl: 'https://maps.app.goo.gl/1bEGRUPtEwH8o1dV9' }
  ];
  const footerMenu = menu.length
    ? menu.filter((item) => item.slug !== 'homepage')
    : [
        { title: 'About Us', slug: 'about-us' },
        { title: 'Education Program', slug: 'education-program' },
        { title: 'Admissions', slug: 'admissions' },
        { title: 'Student Life', slug: 'student-life' },
        { title: 'Facilities', slug: 'facilities' },
        { title: 'News and Updates', slug: 'news-and-updates' },
        { title: 'Parents', slug: 'parents' },
        { title: 'Careers', slug: 'careers' },
        { title: 'Contact Us', slug: 'contact-us' },
      ];
  const headerMenu = (menu.length
    ? menu
    : [
        { title: 'About Us', slug: 'about-us' },
        { title: 'Education Program', slug: 'education-program' },
        { title: 'Admissions', slug: 'admissions' },
        { title: 'Student Life', slug: 'student-life' },
        { title: 'Facilities', slug: 'facilities' },
        { title: 'News and Updates', slug: 'news-and-updates' },
        { title: 'Parents', slug: 'parents' },
        { title: 'Careers', slug: 'careers' },
      ]).filter((item) => item.slug !== 'contact-us');

  // Extract footer data from CMS for dynamic display
  // Falls back to hardcoded defaults if not in CMS or not on homepage
  const [footerData, setFooterData] = useState(null);

  // Fetch footer section data when homepage is active
  useEffect(() => {
    if (activeSlug !== 'homepage') {
      setFooterData(null);
      return;
    }

    const fetchFooterData = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/homepage/cms/sections?published=true&_t=${Date.now()}`,
          { credentials: 'omit' }
        );
        if (response.ok) {
          const result = await response.json();
          const sections = Array.isArray(result.data) ? result.data : Object.values(result.data || {});
          const footerSection = sections.find(s => s.section_type === 'footer');
          setFooterData(footerSection || null);
        }
      } catch (err) {
        console.error('[App] Error fetching footer data:', err);
      }
    };

    // Initial fetch
    fetchFooterData();

    const handleApprovalComplete = () => {
      fetchFooterData();
      ['about-us', 'education-program', 'secondary', 'admissions', 'student-life', 'facilities', 'news-and-updates', 'news-updates', 'parents', 'careers'].forEach(slug => {
        try { sessionStorage.removeItem('cms_page_' + slug); } catch (e) {}
      });
      setPageRefreshCounter(prev => prev + 1);
    };

    window.addEventListener('approvalCompleted', handleApprovalComplete);
    return () => window.removeEventListener('approvalCompleted', handleApprovalComplete);
  }, [activeSlug]);

  const footerSection = footerData;
  const footerSource = footerSection 
    ? footerSection.content
    : null;

  const footerParsed = (() => {
    if (!footerSource) return null;
    if (typeof footerSource === 'string') {
      try {
        return JSON.parse(footerSource);
      } catch (e) {
        console.error('[App] Error parsing footer data:', e);
        return null;
      }
    }
    return footerSource;
  })();

  const footerContent = footerParsed ? (footerParsed[language] || footerParsed.en || footerParsed) : null;

  // Prepare footer display data from CMS or hardcoded defaults
  const campusOneLabel = footerContent?.campus_1_label || 'Campus 1';
  const campusOneAddress = footerContent?.campus_1_address || 'Jl. Pos Pengumben Lama No.13, Srengseng, Kec. Kembangan, Kota Jakarta Barat';
  const campusTwoLabel = footerContent?.campus_2_label || 'Campus 2';
  const campusTwoAddress = footerContent?.campus_2_address || 'Jl. Srengseng Raya No.69, Srengseng, Kec. Kembangan, Kota Jakarta Barat';
  const phoneNumber = footerContent?.phone_number || '(021) 5890 1100';
  const whatsappNumber = footerContent?.whatsapp_number || '+62 815 1759 3512';
  const instagramUrl = footerContent?.instagram_url || 'https://instagram.com/ph_secondary';
  const facebookUrl = footerContent?.facebook_url || 'https://facebook.com/permataharapan';
  const youtubeUrl = footerContent?.youtube_url || 'https://youtube.com/permataharapan';
  const campusOneMapsUrl = footerContent?.campus_1_maps || 'https://maps.app.goo.gl/1bEGRUPtEwH8o1dV9';
  const campusTwoMapsUrl = footerContent?.campus_2_maps || 'https://maps.app.goo.gl/1bEGRUPtEwH8o1dV9';

  if (isDashboardView) {
    return isUnifiedGatePath ? (
      <UnifiedAccessPortal apiBase={API_BASE} />
    ) : (
      <LoginGateway apiBase={API_BASE} />
    );
  }

  return (
    <div className={`app-shell ${isDashboardView ? 'app-shell-dashboard' : ''}`}>
      {!isUnifiedGatePath && !isDashboardPath && activeSlug !== 'administrator' && activeSlug !== 'principal-dashboard' && activeSlug !== 'website-admin-cms' && !isParentDashboard && !isStaffDashboard && !isLogin && !isRegister && (
        <Navbar
          headerMenu={headerMenu}
          activeItemId={activeItemId}
          activeSlug={activeSlug}
          onSelectSlug={onSelectSlug}
          goToSlug={goToSlug}
          goToHomepage={goToHomepage}
          getSubmenuItemsForNavItem={getSubmenuItemsForNavItem}
          goToSection={goToSection}
          loadingMenu={loadingMenu}
        />
      )}

      <main className={activeMainClass}>
        <ErrorBoundary>
        {isUnifiedGatePath ? (
          <UnifiedAccessPortal apiBase={API_BASE} />
        ) : isDashboardPath || activeSlug === 'administrator' || activeSlug === 'principal-dashboard' || activeSlug === 'website-admin-cms' ? (
          // For path-based dashboard routing (e.g., /administrator, /website-admin-cms, /principal-dashboard),
          // use LoginGateway to handle session checking and route to appropriate dashboard
          <LoginGateway apiBase={API_BASE} />
        ) : isTestContent ? (
          <TestContentPage />
        ) : isHomepage ? (
          <HomepageCacheProvider>
            <HomePage language={language} onLanguageChange={setLanguage} onNavigate={goToSlug} />
          </HomepageCacheProvider>
        ) : isAboutUs ? (
          <div className="about-us-container">
            {error && <div className="alert alert-error">{error}</div>}
            {(loadingPage || !page || page._slug !== 'about-us') ? (
              <div className="page-loading-skeleton">
                <div className="loading-spinner"></div>
                <p>Loading About Us content...</p>
              </div>
            ) : (
              <AboutUsPage page={page} />
            )}
          </div>
        ) : isEducationProgram ? (
          <div className="education-program-container">
            {error && <div className="alert alert-error">{error}</div>}
            {(loadingPage || !page || page._slug !== 'education-program') ? (
              <div className="page-loading-skeleton">
                <div className="loading-spinner"></div>
                <p>Loading Education Program content...</p>
              </div>
            ) : (
              <EducationProgramPage page={page} onNavigate={goToSection} />
            )}
          </div>
        ) : isAdmissions ? (
          <div className="admissions-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {(loadingPage || !page || page._slug !== 'admissions') ? (
              <div className="page-loading-skeleton">
                <div className="loading-spinner"></div>
                <p>Loading Admissions content...</p>
              </div>
            ) : (
              <AdmissionsPage page={page} />
            )}
          </div>
        ) : isStudentLife ? (
          <div className="student-life-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {(loadingPage || !page || page._slug !== 'student-life') ? (
              <div className="page-loading-skeleton">
                <div className="loading-spinner"></div>
                <p>Loading Student Life content...</p>
              </div>
            ) : (
              <StudentLifePage page={page} onNavigate={goToSlug} />
            )}
          </div>
        ) : isFacilities ? (
          <div className="facilities-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && !page ? (
              <div className="page-loading">Loading Facilities content...</div>
            ) : (
              <FacilitiesPage page={page} onNavigate={goToSlug} />
            )}
          </div>
        ) : isFacilityDetail ? (
          <div className="facility-detail-page-container">
            {(() => {
              const facilityId = activeSlug.replace('facility-', '');
              return <FacilityDetailPage facilityId={facilityId} onNavigate={goToSlug} />;
            })()}
          </div>
        ) : isTechnologyDetail ? (
          <div className="technology-detail-page-container">
            {(() => {
              const technologyId = activeSlug.replace('technology-', '');
              return <TechnologyDetailPage technologyId={technologyId} onNavigate={goToSlug} />;
            })()}
          </div>
        ) : isNewsUpdates ? (
          <div className="news-updates-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            <NewsUpdatesPage page={page} onNavigate={goToSlug} />
          </div>
        ) : isParents ? (
          <div className="parents-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            <ParentsPage page={page} onNavigate={goToSlug} />
          </div>
        ) : isPaLCDetail ? (
          <div className="palc-detail-page-container">
            {!loadingPage && <PaLCDetailPage palcId={activeSlug.replace('palc-', '')} onNavigate={goToSlug} />}
          </div>
        ) : isCareers ? (
          <div className="careers-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            <CareersPage page={page} />
          </div>
        ) : isContactUs ? (
          <div className="contact-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {(loadingPage || !page || page._slug !== 'contact-us') ? (
              <div className="page-loading-skeleton">
                <div className="loading-spinner"></div>
                <p>Loading Contact Us content...</p>
              </div>
            ) : (
              <ContactUsPage page={page} />
            )}
          </div>
        ) : isRegister ? (
          <RegisterPage language={language} onNavigate={goToSlug} />
        ) : isLogin || isParentDashboard || isStaffDashboard ? (
          <LoginGateway apiBase={API_BASE} />
        ) : isNewsAll ? (
          <NewsAllPage language={language} />
        ) : isNewsDetail ? (
          <NewsDetailPage language={language} newsId={parseInt(activeSlug.split('/')[1])} onNavigate={goToSlug} page={page} />
        ) : isNewsPressDetail ? (
          <NewsDetailPage newsId={activeSlug.replace('news-press-', '')} onNavigate={goToSlug} page={page} />
        ) : isNewsBlogDetail ? (
          <NewsDetailPage newsId={activeSlug.replace('news-blog-', '')} onNavigate={goToSlug} page={page} />
        ) : isEventsCompetitions ? (
          <div className="events-page-container">
            {error && <div className="alert alert-error">{error}</div>}
            <EventsCompetitionsPage page={page} />
          </div>
        ) : isPreschool ? (
          <div className="preschool-page-container">
            <PreschoolPage onNavigate={goToSlug} />
          </div>
        ) : isKindergarten ? (
          <div className="kindergarten-page-container">
            <KindergartenPage onNavigate={goToSlug} />
          </div>
        ) : isPrimary ? (
          <div className="primary-page-container">
            <PrimaryPage onNavigate={goToSlug} />
          </div>
        ) : isSecondary ? (
          <div className="secondary-page-container">
            {(loadingPage || !page || page._slug !== 'secondary') ? (
              <div className="page-loading-skeleton">
                <div className="loading-spinner"></div>
                <p>Loading Secondary content...</p>
              </div>
            ) : (
              <SecondaryPage page={page} onNavigate={goToSlug} />
            )}
          </div>
        ) : isHouseDetail ? (
          <div className="house-detail-page-container">
            {(() => {
              return <HouseDetailPage houseName={activeSlug} onNavigate={goToSection} />;
            })()}
          </div>
        ) : (
          <div className="page-container">
            {error && <div className="alert alert-error">{error}</div>}
            {loadingPage && <div className="alert alert-info">Loading page...</div>}

            {!loadingPage && page ? (
              <article className="page-article">
                <header className="article-header">
                  <h2>{page.title}</h2>
                  <p className="article-summary">{page.summary}</p>
                  <div className="article-meta">
                    <span className="badge">{page.status_code}</span>
                    <span className="meta-text">{page.updated_at}</span>
                  </div>
                </header>

                <section
                  className="article-body"
                  dangerouslySetInnerHTML={{ __html: page.body || '<p>No content body yet.</p>' }}
                />
              </article>
            ) : null}
          </div>
        )}
        </ErrorBoundary>
      </main>

      {!isUnifiedGatePath && !isRegister && !isLogin && !isDashboardPath && activeSlug !== 'principal-dashboard' && activeSlug !== 'website-admin-cms' && !isParentDashboard && !isStaffDashboard && (
        <footer className="site-footer">
          <div className="site-footer-shell">
            {/* Column 1: Brand & Campuses & Contact */}
            <div className="site-footer-col site-footer-brand">
              <div className="site-footer-brand-lockup">
                <img src="/logo_phs.png" alt="Permata Harapan Secondary Logo" className="site-footer-logo" />
                <div className="site-footer-brand-text">
                  <h3>Permata Harapan</h3>
                  <span className="site-footer-school-level">Secondary School</span>
                  <p className="site-footer-tagline">School for Life</p>
                </div>
              </div>

              {/* Campus Addresses */}
              <div className="site-footer-campuses">
                <div className="site-footer-campus-item">
                  <div className="site-footer-campus-header">
                    <MapPin size={14} className="site-footer-pin-icon" />
                    <h5>{campusOneLabel}</h5>
                  </div>
                  <p>{campusOneAddress}</p>
                </div>
                <div className="site-footer-campus-item">
                  <div className="site-footer-campus-header">
                    <MapPin size={14} className="site-footer-pin-icon" />
                    <h5>{campusTwoLabel}</h5>
                  </div>
                  <p>{campusTwoAddress}</p>
                </div>
              </div>

              {/* Direct Contact Links */}
              <div className="site-footer-contact">
                <a 
                  href={`tel:${phoneNumber.replace(/\D/g, '')}`}
                  className="contact-link-with-icon"
                  title="Call Admissions"
                >
                  <span className="contact-icon-circle"><Phone size={14} /></span>
                  <span>{phoneNumber}</span>
                </a>
                <a 
                  href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noreferrer"
                  className="contact-link-with-icon"
                  title="Chat via WhatsApp"
                >
                  <span className="contact-icon-circle contact-icon-wa"><MessageCircle size={14} /></span>
                  <span>{whatsappNumber}</span>
                </a>
                <a 
                  href="mailto:admissions@permataharapansecondary.sch.id"
                  className="contact-link-with-icon"
                  title="Email Admissions"
                >
                  <span className="contact-icon-circle"><Mail size={14} /></span>
                  <span>admissions@permataharapansecondary.sch.id</span>
                </a>
              </div>
            </div>

            {/* Column 2: Interactive Main Menu */}
            <div className="site-footer-col site-footer-links">
              <h4 className="site-footer-heading">
                <span>Main Navigation</span>
                <span className="site-footer-heading-line"></span>
              </h4>
              <nav className="site-footer-nav-grid" aria-label="Footer Navigation">
                {footerMenu.map((item) => (
                  <button
                    key={item.slug || item.title}
                    className={`site-footer-nav-btn ${activeSlug === item.slug ? 'active' : ''}`}
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      onSelectSlug(item);
                    }}
                    type="button"
                  >
                    <span className="site-footer-nav-bullet"></span>
                    <span>{item.title}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Column 3: School Info, Accreditation & Social Channels */}
            <div className="site-footer-col site-footer-info">
              {/* Cambridge Accreditation */}
              <div className="site-footer-info-block">
                <h4 className="site-footer-heading">
                  <span>Accreditation</span>
                  <span className="site-footer-heading-line"></span>
                </h4>
                <div className="site-footer-accreditation-card">
                  <img
                    src="/cambridge-logo.webp"
                    alt="Cambridge Assessment International Education"
                    className="site-footer-accreditation-logo"
                  />
                  <div className="site-footer-accreditation-details">
                    <span className="site-footer-accreditation-caption">Cambridge International School</span>
                    <span className="site-footer-accreditation-sub">Registered International Curriculum</span>
                  </div>
                </div>
              </div>

              {/* Campus Locations Directions Badges */}
              <div className="site-footer-info-block">
                <h4 className="site-footer-heading">
                  <span>Campus Directions</span>
                  <span className="site-footer-heading-line"></span>
                </h4>
                <div className="site-footer-campus-badges">
                  {campusLinks.map((campus) => (
                    <a
                      key={campus.name}
                      href={campus.mapsUrl || '#'}
                      target="_blank"
                      rel="noreferrer"
                      className="site-footer-campus-badge"
                      title={`Open ${campus.name} on Google Maps`}
                    >
                      <MapPin size={13} />
                      <span>{campus.name} Maps</span>
                      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M7 17l9.2-9.2M17 17V8H8"/></svg>
                    </a>
                  ))}
                </div>
              </div>

              {/* Connect With Us */}
              <div className="site-footer-info-block">
                <h4 className="site-footer-heading">
                  <span>Connect With Us</span>
                  <span className="site-footer-heading-line"></span>
                </h4>
                <div className="social-icons-container">
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="social-icon-link social-icon-ig"
                    title="Follow on Instagram"
                    aria-label="Instagram"
                  >
                    <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                      <rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
                      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor"/>
                    </svg>
                  </a>
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="social-icon-link social-icon-yt"
                    title="Subscribe on YouTube"
                    aria-label="YouTube"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                  <a
                    href={facebookUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="social-icon-link social-icon-fb"
                    title="Follow on Facebook"
                    aria-label="Facebook"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a
                    href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                    className="social-icon-link social-icon-wa"
                    title="Chat on WhatsApp"
                    aria-label="WhatsApp"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                      <path d="M12.031 0C5.395 0 .017 5.378.017 12.015c0 2.12.553 4.19 1.603 6.01L.003 24l6.15-1.613a11.954 11.954 0 0 0 5.878 1.536h.005c6.632 0 12.01-5.378 12.01-12.015 0-3.208-1.25-6.223-3.518-8.492C18.26 1.25 15.244 0 12.031 0zm-.005 21.848a9.837 9.837 0 0 1-5.016-1.374l-.36-.214-3.731.978.996-3.637-.235-.374a9.832 9.832 0 0 1-1.508-5.216c0-5.434 4.42-9.854 9.859-9.854 2.633 0 5.108 1.026 6.969 2.888a9.799 9.799 0 0 1 2.883 6.969c0 5.434-4.421 9.854-9.857 9.854zm5.405-7.382c-.296-.148-1.753-.865-2.025-.964-.271-.099-.469-.148-.666.148-.198.297-.766.964-.939 1.162-.173.198-.346.223-.642.074-.296-.148-1.25-.46-2.381-1.47-.88-.785-1.474-1.756-1.647-2.053-.173-.297-.018-.458.13-.605.134-.133.296-.346.444-.519.148-.173.198-.297.296-.495.099-.198.05-.371-.025-.519-.074-.148-.666-1.608-.913-2.201-.241-.578-.487-.5-.666-.508l-.568-.01c-.198 0-.519.074-.79.371-.272.297-1.037 1.014-1.037 2.474 0 1.46 1.062 2.87 1.21 3.068.148.198 2.09 3.193 5.064 4.478.708.306 1.26.488 1.691.625.711.226 1.358.194 1.87.118.571-.085 1.753-.717 2.001-1.41.247-.693.247-1.287.173-1.411-.074-.124-.272-.198-.568-.346z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright Bottom Bar */}
          <div className="site-footer-bottom">
            <div className="site-footer-bottom-inner">
              <p className="site-footer-copyright">
                © {new Date().getFullYear()} Permata Harapan Secondary School. All Rights Reserved.
              </p>
              <p className="site-footer-motto">
                Nurturing Character, Excellence, and Global Mindset.
              </p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;